import {
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { CustomCellEditorProps } from 'ag-grid-react'
import { styled } from '@mui/system'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import { filterByCombination } from '../../../../../../../../lib/commons/customEnum'
import objects from '../../../../../../../../utils/objects'
import { Color } from '../../../../../../../../styles/commonStyles'
import ViewMeta from '../../../../../../meta/ViewMeta'
import { FloatingEditableIcon } from '../text'
import Select from '../../../../../../../components/editors/select/Select'
import { RootDiv } from '../../CellRendererRoot'
import { MenuItem, SelectChangeEvent } from '@mui/material'

const getDefaultLabel = (uiMeta: FunctionProperty) => {
  const defaultValue = uiMeta.valuesAllowed.find(
    item => item.value === uiMeta.defaultValue
  )
  return defaultValue?.name || ''
}

const getLabel = (uiMeta: FunctionProperty, value?: string) => {
  const valuesAllowed = uiMeta.valuesAllowed
  if (!valuesAllowed) {
    return ''
  }
  const option = valuesAllowed.find(item => item.value === value)
  return option ? option.name : getDefaultLabel(uiMeta)
}

export const selectCellValueGetter = (params: ValueGetterParams): string => {
  if (!params.data) {
    return ''
  }
  const uiMeta: FunctionProperty = params.colDef.cellEditorParams.uiMeta
  const defaultValue = uiMeta.defaultValue
  let value
  if (uiMeta.entityExtensionUuid) {
    if (!params.data.extensions) {
      return ''
    }
    const entityExtension = params.data.extensions.find(
      v => v.uuid === uiMeta.entityExtensionUuid
    )
    value = entityExtension?.value
  } else {
    value = objects.getValue(params.data, params.colDef.field) || defaultValue
  }
  return value === 'NONE' ? '' : value
}

export const selectCellRenderer = (props: ICellRendererParams) => {
  if (!props?.colDef) return <></>
  const uiMeta = props.colDef.cellRendererParams.uiMeta
  const valuesAllowed = uiMeta.valuesAllowed
  if (!valuesAllowed) return <></>

  const option = valuesAllowed.find(item => item.value === props.value)

  return (
    <RootDiv>
      {option && option.iconUrl && (
        <img src={option.iconUrl} className="sevend-ag-cell-select-icon" />
      )}
      {props.valueFormatted}
      <FloatingEditableIcon {...props} />
    </RootDiv>
  )
}

export const selectCellValueFormatter = (params: ValueFormatterParams) => {
  return getLabel(params.colDef.cellEditorParams.uiMeta, params.value)
}

export const selectCellFilterValueFormatter = (
  params: ValueFormatterParams
) => {
  const value = getLabel(params.colDef.cellRendererParams.uiMeta, params.value)
  if (!value.trim()) {
    return '(Blanks)'
  }
  return value
}

// Styles
const StyledSelect = styled(Select)({
  width: '100%',
  height: '100%',
})
const StyledMenuItem = styled(MenuItem)<{ value?: string }>(({ value }) => {
  let style = {
    whiteSpace: 'nowrap',
    '&:hover': {
      color: Color.MAIN,
      backgroundColor: `transparent !important`,
    },
  } as any
  if (value === 'NONE') {
    style = {
      ...style,
      position: 'absolute',
      top: '-50px',
      height: '0',
    }
  }
  return style
}) as typeof MenuItem

interface Props extends CustomCellEditorProps {
  eventKey: string | null

  uiMeta: FunctionProperty
  viewMeta: ViewMeta
}

const SelectCellEditor = (props: Props) => {
  const [value, setValue] = useState(props.value as string)
  const [open, setOpen] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  const options = useMemo(() => {
    const getCombinedValue = (path: string) => {
      return objects.getValue(props.node.data, path)
    }
    return filterByCombination(props.uiMeta.valuesAllowed, getCombinedValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ算出。セル編集中に valuesAllowed は変化しない
  }, [])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
    if (props.eventKey === 'Delete' || props.eventKey === 'Backspace') {
      // Delete key event is handled at BulkSheet.onDelete()
      setValue('')
      props.onValueChange('')
      props.api.clearFocusedCell()
    } else {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  const handleValueChanged = (
    event: SelectChangeEvent<unknown>,
    _child: React.ReactNode
  ) => {
    const newValue = event.target.value as string
    props.onValueChange(newValue)
    setValue(newValue)
    setOpen(false)
    props.api.stopEditing()
  }

  const handleClosed = (keyCode: number) => {
    setOpen(false)
    setTimeout(() => {
      if (keyCode === 9) {
        props.api.tabToNextCell()
        return
      }
      let nextRowIndex
      const current = props.api.getFocusedCell()
      if (!current) return
      if (keyCode === 13) {
        nextRowIndex =
          props.api.getLastDisplayedRowIndex() !== current.rowIndex
            ? current.rowIndex + 1
            : current.rowIndex
      } else {
        nextRowIndex = current.rowIndex
      }
      props.api.stopEditing()
      props.api.clearCellSelection()
      props.api.clearFocusedCell()
      props.api.setFocusedCell(nextRowIndex, current.column)
    }, 100)
  }

  const handleKeyDownOption = (event: React.KeyboardEvent) => {
    // When Tab key is pressed, set the selected option as value
    if (event.keyCode === 9) {
      const selected = (event.target as HTMLElement).dataset.value
      handleValueChanged(
        {
          ...event,
          target: {
            ...event.target,
            value: selected,
          },
        } as any,
        props.node.data
      )
    }
  }

  if (options.length === 0) {
    return <></>
  }
  const hasIcon = options.filter(option => option.iconUrl).length
  return (
    <StyledSelect
      ref={inputRef}
      value={value}
      options={options}
      open={open}
      onChange={handleValueChanged}
      onClose={event => {
        // @ts-ignore
        handleClosed(event.keyCode)
      }}
      defaultValue={props.uiMeta.defaultValue}
      getOptionIconUrl={hasIcon ? o => o.iconUrl : undefined}
      getOptionLabel={o => o.name}
      getOptionDisabled={o => o.value === 'NONE'}
      onOptionKeyDown={handleKeyDownOption}
      MenuItem={StyledMenuItem}
      MenuProps={{
        anchorReference: 'anchorEl',
        anchorEl: props.eGridCell,
      }}
    />
  )
}

export default SelectCellEditor
